import { useMemo, useCallback } from 'react';
import { useApi } from '../../provider';
import { useQuery, useMutation } from '../../hooks';
import { queryCache } from '../../cache';
import { ManufacturerService } from './service';
import { MergeService } from '../merge/service';
import { MANUFACTURERS_MERGE_CONFIG } from '../merge/config';
import { useMergeSuggestions, useMergePreview } from '../merge/hooks';
import {
  Manufacturer,
  CreateManufacturerRequest,
  UpdateManufacturerRequest,
  ManufacturerResponse,
  ManufacturersQueryParams,
  ManufacturerQueryResponse,
} from './types';

const CACHE_KEY = 'manufacturers';

export function useManufacturers(params?: ManufacturersQueryParams) {
  // TODO: Add proper return types
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  // Create a cache key that includes query parameters
  const cacheKey = useMemo(() => {
    return params
      ? `${CACHE_KEY}?${new URLSearchParams(params as Record<string, string>)}`
      : CACHE_KEY;
  }, [params]);

  const fetcher = useCallback(() => service.getAll(params), [service, params]);

  return useQuery<ManufacturerQueryResponse>(cacheKey, fetcher);
}

export function useManufacturer(id: number) {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);
  const fetcher = useCallback(() => service.getById(id), [service, id]);

  return useQuery<Manufacturer>(`${CACHE_KEY}/${id}`, fetcher);
}

export function useCreateManufacturer() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<ManufacturerResponse, CreateManufacturerRequest>(
    data => service.create(data),
    {
      // When a new manufacturer is created, invalidate the manufacturers list
      onSuccess: () => {
        queryCache.invalidate(CACHE_KEY);
      },
    }
  );
}

export function useUpdateManufacturer() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<ManufacturerResponse, UpdateManufacturerRequest>(
    data => service.update(data),
    {
      // When a manufacturer is updated, invalidate both the list and the individual manufacturer
      onSuccess: data => {
        queryCache.invalidate(CACHE_KEY);
        queryCache.invalidate(`${CACHE_KEY}/${data.manufacturer_id}`);
      },
    }
  );
}

export function useDeleteManufacturer() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<void, number>(id => service.delete(id), {
    // When a manufacturer is deleted, invalidate the manufacturers list
    onSuccess: (_, id) => {
      queryCache.invalidate(CACHE_KEY);
      queryCache.invalidate(`${CACHE_KEY}/${id}`);
    },
  });
}

// Convenience hook that combines all manufacturers operations
export function useManufacturersPage(params?: ManufacturersQueryParams) {
  const manufacturersResponse = useManufacturers(params);
  const createManufacturer = useCreateManufacturer();
  const updateManufacturer = useUpdateManufacturer();
  const deleteManufacturer = useDeleteManufacturer();

  return useMemo(
    () => ({
      // Query operations
      manufacturers: manufacturersResponse,

      // Mutation operations
      create: createManufacturer,
      update: updateManufacturer,
      delete: deleteManufacturer,

      // Convenience getters for common states
      data:
        manufacturersResponse.status === 'success'
          ? (manufacturersResponse.data?.manufacturers ?? [])
          : [],
      total:
        manufacturersResponse.status === 'success'
          ? (manufacturersResponse.data?.total ?? 0)
          : 0,
      isLoading: manufacturersResponse.status === 'loading',
      isError: manufacturersResponse.status === 'error',
      error:
        manufacturersResponse.status === 'error'
          ? manufacturersResponse.error
          : null,

      // Actions
      refetch: manufacturersResponse.refetch,
      invalidate: manufacturersResponse.invalidate,
    }),
    [
      manufacturersResponse,
      createManufacturer,
      updateManufacturer,
      deleteManufacturer,
    ]
  );
}

// Merge-specific hooks for Manufacturers
export function useMergeManufacturersSuggestions(
  sourceId: number | null,
  maxResults = 10
) {
  return useMergeSuggestions<Manufacturer>(
    MANUFACTURERS_MERGE_CONFIG,
    sourceId,
    maxResults
  );
}

export function useMergeManufacturersPreview(
  sourceId: number | null,
  targetId: number | null
) {
  return useMergePreview(MANUFACTURERS_MERGE_CONFIG, sourceId, targetId);
}

export function useMergeManufacturers() {
  const api = useApi();
  const mergeService = useMemo(
    () => new MergeService(api, MANUFACTURERS_MERGE_CONFIG),
    [api]
  );

  return useMutation(
    async (variables: {
      sourceId: number;
      targetId: number;
      request: {
        new_canonical_name: string;
        user_id: string;
        merge_reason?: string;
      };
    }) => {
      return mergeService.executeMerge(
        variables.sourceId,
        variables.targetId,
        variables.request
      );
    },
    {
      onSuccess: () => {
        // Invalidate manufacturers cache after successful merge
        queryCache.invalidate(CACHE_KEY);
      },
    }
  );
}
